import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, Megaphone, X } from "lucide-react";
import { WhatsNewEntry } from "@/types/domain";
import { MenuIcon } from "../side-nav/MenuIcon";

type WhatsNewModalProps = {
  entries: WhatsNewEntry[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const navButtonClass =
  "inline-flex h-9 min-w-27 items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium outline-none cursor-pointer " +
  "focus-visible:ring-2 focus-visible:ring-(--layout-side-nav-active-bg)/50";

const kbdClass = "rounded border border-b-2 border-slate-300 bg-white px-1.5 py-px text-[0.85em] font-semibold text-slate-700";

const withKeys = (text: string) =>
  text.split("Ctrl K").flatMap((part, i) => (i === 0 ? [part] : [<kbd key={i} className={kbdClass}>Ctrl K</kbd>, part]));

export const WhatsNewModal = ({ entries, open, onOpenChange }: WhatsNewModalProps) => {
  const [index, setIndex] = useState(0);
  const total = entries.length;
  const isLast = index === total - 1;
  const entry = entries[index];

  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") setIndex((current) => Math.min(total - 1, current + 1));
      else if (event.key === "ArrowLeft") setIndex((current) => Math.max(0, current - 1));
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, total]);

  if (!entry) return null;

  const next = () => (isLast ? onOpenChange(false) : setIndex(index + 1));

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[3px]" />
        <DialogPrimitive.Content
          data-testid="whats-new-modal"
          aria-describedby="whats-new-description"
          className="fixed left-1/2 top-6 z-50 flex max-h-[calc(100vh-48px)] w-[860px] max-w-[calc(100vw-32px)] -translate-x-1/2 flex-col overflow-hidden rounded-2xl bg-white text-sm text-slate-800 shadow-[0_32px_80px_rgba(15,23,42,0.45),0_0_0_1px_rgba(15,23,42,0.06)] outline-none"
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center gap-2.5 pl-6 pr-4 pt-4">
            <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-white">
              <Megaphone className="size-3.5" aria-hidden />
            </span>
            <div className="flex-1">
              <DialogPrimitive.Title className="m-0 text-[13px] font-semibold uppercase leading-snug tracking-widest text-white">What&apos;s new</DialogPrimitive.Title>
              <DialogPrimitive.Description id="whats-new-description" className="sr-only">
                Recent improvements to Klipboard CRM
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              aria-label="Close"
              className="pointer-events-auto inline-flex size-8 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 cursor-pointer"
            >
              <X className="size-4" aria-hidden />
            </DialogPrimitive.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div
              className="relative flex h-55 items-center justify-center overflow-hidden"
              style={{
                backgroundColor: "var(--layout-side-nav-bg)",
                backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(135deg, var(--layout-side-nav-bg), color-mix(in srgb, var(--layout-side-nav-bg), white 12%))",
                backgroundSize: "18px 18px, auto",
              }}
            >
              <span
                aria-hidden
                className="absolute -right-15 -top-22 size-85 rounded-full"
                style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--layout-side-nav-active-bg) 55%, transparent), transparent 70%)" }}
              />
              <span
                aria-hidden
                className="absolute -bottom-30 -left-20 size-80 rounded-full"
                style={{ background: "radial-gradient(circle, rgba(255,255,255,0.14), transparent 70%)" }}
              />
              <span className="whats-new-hero-icon relative inline-flex size-24 items-center justify-center rounded-[26px] bg-(--layout-side-nav-active-bg) text-white shadow-[0_0_0_6px_rgba(255,255,255,0.18),0_18px_36px_rgba(15,23,42,0.4)] [&_svg]:size-10">
                <MenuIcon name={entry.icon} />
              </span>
            </div>
            <div className="min-h-72 px-10 pb-8 pt-7">
              <span className="inline-block rounded-full bg-(--layout-side-nav-active-bg)/10 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-(--layout-side-nav-active-bg)">{entry.area}</span>
              <h3 className="mb-0 mt-3 text-[28px] font-bold leading-tight text-slate-900">{entry.title}</h3>
              <p className="mb-0 mt-2.5 text-base leading-relaxed text-slate-600">{withKeys(entry.summary)}</p>
              <ul className="m-0 mt-5 list-none p-0">
                {entry.points.map((point) => (
                  <li key={point} className="mt-2 flex items-start gap-3 rounded-[10px] bg-slate-50 px-3.5 py-2.5 text-sm leading-normal text-slate-800">
                    <span className="inline-flex size-5.5 shrink-0 items-center justify-center rounded-full bg-green-600/15 text-green-600">
                      <Check className="size-3" aria-hidden />
                    </span>
                    <span>{withKeys(point)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-slate-200 px-5 py-3.5">
            <button
              type="button"
              disabled={index === 0}
              onClick={() => setIndex(index - 1)}
              className={`${navButtonClass} border-slate-300 bg-white text-slate-800 hover:bg-slate-100 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-white`}
            >
              Previous
            </button>
            <div className="flex flex-col items-center gap-1.5">
              <div className="flex gap-1.5">
                {entries.map((e, i) => (
                  <button
                    key={e.id}
                    type="button"
                    aria-label={`Go to ${e.title}`}
                    onClick={() => setIndex(i)}
                    className={`h-2 cursor-pointer rounded-full border-0 p-0 transition-all ${i === index ? "w-6 bg-(--layout-side-nav-active-bg)" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
                  />
                ))}
              </div>
              <span className="text-xs text-slate-500">{index + 1} of {total}</span>
            </div>
            <button
              type="button"
              onClick={next}
              className={`${navButtonClass} border-(--layout-side-nav-active-bg) bg-(--layout-side-nav-active-bg) text-white hover:brightness-95`}
            >
              {isLast ? "Done" : "Next"}
            </button>
          </div>

          <div data-testid="whats-new-hints" className="border-t border-slate-200 bg-slate-50 px-5 py-2.5 text-xs text-slate-500">
            <p className="m-0"><kbd className="rounded bg-slate-200 px-1.5 text-[11px] font-medium text-slate-700">{/Mac|iPhone|iPad/.test(navigator.platform) ? "Cmd" : "Ctrl"} K</kbd> jumps to any page or setting.</p>
            <p className="m-0 mt-1">Find this again any time in your user menu (top right) under What&apos;s new.</p>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
